import { z } from "zod";
import { PaginatedOrganizationsDocument, PaginatedOrganizationsQuery, PaginatedOrganizationsQueryVariables } from "@/graphql/__generated__/graphql";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Controller } from "react-hook-form";
import { CustomCombobox } from "@/components/shared";
import { Label } from "@/components/ui/label";
import { JSX, useEffect } from "react";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client/react";

interface Props {
  form: UseFormReturn<any>;
  className?: string;
}

type SelectOption = {
  label: string;
  value: number;
}
export const createOperationalOrganizationIdsSchema = (t: TFunction) => {
  return z.object({
    operationalOrganizationIds: z.array(z.number(), {
      error: (issue) => issue.input === undefined ?
        t("newtender.formgroups.operationalorganizations.requirederror")
        :
        t("newtender.formgroups.operationalorganizations.invalidtypeerror")
    }),
  });
};

export const operationalOrganizationIdsDefaultValues = {
  operationalOrganizationIds: []
};

/**
 * OperationalOrganizationsFormGroup component.
 *
 * This component renders a multi-select input field for selecting operational organizations.
 * It uses the `usePaginatedOrganizationsQuery` hook to fetch a paginated list of organizations.
 *
 * @param {Props} props - The props for the component.
 * @param {object} props.form - The form object containing control methods.
 *
 * @returns {JSX.Element} The rendered component.
 */
const OperationalOrganizationsFormGroup = ({
  form,
  className
}: Props): JSX.Element => {

  // Hooks
  const { t } = useTranslation();
  const { data, loading } = useQuery<PaginatedOrganizationsQuery, PaginatedOrganizationsQueryVariables>(PaginatedOrganizationsDocument, {
    variables: {
      first: 100,
      after: null,
      filters: {
        isOperational: true,
      },
    },
  });

  // Map organizations to SelectOption
  const organizations: SelectOption[] = data?.paginatedOrganizations.edges
    .map(edge => edge.node)
    .map(node => ({ label: node.name, value: node.id })) ?? [];

  // Auto-assign operational organization if there's only one option
  useEffect(() => {
    const currentValue = form.getValues("operationalOrganizationIds");
    if (organizations.length === 1 && (!currentValue || currentValue.length === 0)) {
      form.setValue("operationalOrganizationIds", [organizations[0].value], { shouldValidate: true });
    }
  }, [organizations, form]);

  return (
    <Controller
      control={form.control}
      name="operationalOrganizationIds"
      render={({ field }) => (
        <div className={cn("flex flex-col gap-2 form-item", className)}>
          <Label htmlFor="operationalOrganizationIds" className="text-sm font-medium h-[14px]">
            {t("newtender.formgroups.operationalorganizations.label")}
          </Label>
          <CustomCombobox
            value={field.value}
            onChange={field.onChange}
            options={organizations}
            placeholder={t("newtender.formgroups.operationalorganizations.placeholder")}
            loading={loading}
            multiple={true}
            error={!!form.formState.errors.operationalOrganizationIds}
          />
          {form.formState.errors.operationalOrganizationIds && (
            <p className="text-sm text-destructive">
              {t("newtender.formgroups.operationalorganizations.requirederror")}
            </p>
          )}
        </div>
      )}
    />
  );
};

export default OperationalOrganizationsFormGroup;
